import {
	getPageViewMetadata,
	type PageViewMetadata,
} from "@repo/tracking/page-view/lib/page-view-metadata";
import {getSession} from "@repo/actions/GetSession";
import type {DiscoveryEventKey} from "@/app/actions/trackDiscoveryEventUtils";

// Unprefixed to match the funnel event_key convention (site is identified via
// the `app` property instead — see the event spec's naming discussion).
const ROUTE_EVENT_KEYS: {pattern: RegExp; eventKey: string}[] = [
	{pattern: /^\/$/, eventKey: "homepage_viewed"},
	{pattern: /^\/program\/[^/]+\/?$/, eventKey: "program_page_viewed"},
	{pattern: /^\/course\/[^/]+\/?$/, eventKey: "course_page_viewed"},
	{pattern: /^\/catalog\/?$/, eventKey: "catalog_page_viewed"},
];

// Fallback for Sanity-driven discoveryPage routes (app/[slug]/page.tsx) and
// any other Discovery route not covered above.
const DEFAULT_EVENT_KEY = "discovery_page_viewed";

export function getDiscoveryEventKey(pathname: string): string {
	const match = ROUTE_EVENT_KEYS.find(({pattern}) => pattern.test(pathname));
	return match?.eventKey ?? DEFAULT_EVENT_KEY;
}

// Slug-capturing counterparts of the patterns above, scoped to only the
// three "viewed" events actually sent through `trackDiscoveryEvent` today —
// deliberately excludes `catalog_page_viewed`/`discovery_page_viewed` (the
// fallback keys `getDiscoveryEventKey` also produces), since neither is a
// registered `DiscoveryEventKey` and neither has ever been fired.
const PAGE_VIEW_EVENT_PATTERNS: {
	pattern: RegExp;
	eventKey: DiscoveryEventKey;
	buildProperties: (
		match: RegExpMatchArray,
		pathname: string
	) => Record<string, unknown>;
}[] = [
	{
		pattern: /^\/$/,
		eventKey: "homepage_viewed",
		buildProperties: () => ({}),
	},
	{
		pattern: /^\/program\/([^/]+)\/?$/,
		eventKey: "program_page_viewed",
		buildProperties: (match) => ({
			program_slug: match[1],
		}),
	},
	{
		pattern: /^\/course\/([^/]+)\/?$/,
		eventKey: "course_page_viewed",
		buildProperties: (match) => ({
			course_slug: match[1],
		}),
	},
];

/**
 * Maps a client-committed pathname to the Discovery "viewed" event, or
 * `null` for routes that don't fire one (catalog, CMS-driven slugs). Fired
 * from `DiscoveryPageViewMetadataRegistrar` on mount/navigation so Next.js
 * prefetch cannot double-count. See `docs/tracking-strategy.md`.
 */
export function getDiscoveryPageViewEvent(
	pathname: string
): {eventKey: DiscoveryEventKey; properties: Record<string, unknown>} | null {
	for (const {pattern, eventKey, buildProperties} of PAGE_VIEW_EVENT_PATTERNS) {
		const match = pattern.exec(pathname);
		if (match) {
			return {eventKey, properties: buildProperties(match, pathname)};
		}
	}
	return null;
}

async function getDiscoveryPlatformUserId(): Promise<string | null> {
	const session = await getSession({cookiesReadOnly: true});
	return session?.userId ?? null;
}

export async function getDiscoveryPageViewMetadata(
	pathname: string
): Promise<PageViewMetadata> {
	return getPageViewMetadata({
		pathname,
		resolveEventKey: getDiscoveryEventKey,
		getPlatformUserId: getDiscoveryPlatformUserId,
	});
}
